import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Badge,
  Card,
  Col,
  Container,
  Row,
} from 'react-bootstrap';

import {
  NavLink,
} from 'react-router-dom';

import {
  FaEdit,
  FaEye,
} from 'react-icons/fa';

import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import SmartTable from '../../components/Smart/SmartTable';
import SmartTopTool from '../../components/Smart/SmartTopTool';
import SmartPagination from '../../components/Smart/SmartPagination';

import API from '../../api/axios';
import useCompanyContext from '../../hooks/useCompanyContext';

const INITIAL_META = {
  total: 0,
  page: 1,
  pageSize: 10,
  totalPages: 1,
};

const INITIAL_PAGE = {
  page_number: 1,
  total_pages: 1,
};

const INITIAL_EXACT_SEARCH = {
  name: '',
  location: '',
};

const INITIAL_FILTERS = {
  status: [],
};

const EXACT_SEARCH_FIELDS = [
  {
    key: 'name',
    label: 'Shop name',
    placeholder:
      'Exact shop name',
  },
  {
    key: 'location',
    label: 'Location',
    placeholder:
      'Exact location name or slug',
  },
];

const FILTER_OPTIONS = {
  status: [
    'active',
    'inactive',
  ],
};

const normalizeLocation = (
  location,
) => {
  if (!location) {
    return null;
  }

  if (
    typeof location ===
    'string'
  ) {
    return {
      name: location,
      slug: '',
    };
  }

  return location;
};

const ShopsList = () => {
  const {
    currentCompanyID,
    hasSpecificCompany,
    buildCompanyUrl,
  } = useCompanyContext();

  const [rows, setRows] =
    useState([]);

  const [meta, setMeta] =
    useState(
      INITIAL_META,
    );

  const [page, setPage] =
    useState(
      INITIAL_PAGE,
    );

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    globalSearch,
    setGlobalSearch,
  ] = useState('');

  const [
    exactSearch,
    setExactSearch,
  ] = useState(
    INITIAL_EXACT_SEARCH,
  );

  const [
    filters,
    setFilters,
  ] = useState(
    INITIAL_FILTERS,
  );

  const requestRef =
    useRef(0);

  const previousCompanyIdRef =
    useRef(
      currentCompanyID,
    );

  const skipNextFetchRef =
    useRef(false);

  const fetchShops =
    useCallback(
      async () => {
        const requestId =
          ++requestRef.current;

        try {
          setLoading(true);
          setError('');

          const params =
            new URLSearchParams();

          params.set(
            'page',
            String(
              page.page_number ||
              1,
            ),
          );

          params.set(
            'pageSize',
            String(
              meta.pageSize ||
              10,
            ),
          );

          const normalizedGlobalSearch =
            String(
              globalSearch ||
              '',
            ).trim();

          if (
            normalizedGlobalSearch
          ) {
            params.set(
              'q',
              normalizedGlobalSearch,
            );
          }

          Object.entries(
            exactSearch,
          ).forEach(
            ([
              key,
              value,
            ]) => {
              const normalized =
                String(
                  value ||
                  '',
                ).trim();

              if (normalized) {
                params.set(
                  key,
                  normalized,
                );
              }
            },
          );

          const selectedStatus =
            filters.status?.[0]
              ?.toLowerCase();

          if (
            selectedStatus ===
            'active'
          ) {
            params.set(
              'isActive',
              'true',
            );
          }

          if (
            selectedStatus ===
            'inactive'
          ) {
            params.set(
              'isActive',
              'false',
            );
          }

          if (
            hasSpecificCompany
          ) {
            params.set(
              'supplierId',
              String(
                currentCompanyID,
              ),
            );
          }

          const {
            data,
          } =
            await API.get(
              `/shop?${params.toString()}`,
            );

          if (
            requestId !==
            requestRef.current
          ) {
            return;
          }

          const resultRows =
            Array.isArray(
              data?.data,
            )
              ? data.data
              : [];

          const pagination =
            data?.meta ||
            data?.pagination || {
              total:
                data?.total ??
                0,

              page:
                data?.page ??
                1,

              pageSize:
                data?.pageSize ??
                10,

              totalPages:
                data?.totalPages ??
                1,
            };

          setRows(
            resultRows.map(
              (shop) => ({
                id:
                  shop.id,

                name:
                  shop.name,

                isActive:
                  shop.isActive,

                location:
                  normalizeLocation(
                    shop.location ||
                    shop.Location,
                  ),
              }),
            ),
          );

          const nextMeta = {
            total:
              pagination.total ??
              0,

            page:
              pagination.page ??
              1,

            pageSize:
              pagination.pageSize ??
              10,

            totalPages:
              pagination.totalPages ??
              1,
          };

          setMeta(
            nextMeta,
          );

          setPage(
            (prev) => {
              if (
                prev.page_number ===
                nextMeta.page &&
                prev.total_pages ===
                nextMeta.totalPages
              ) {
                return prev;
              }

              return {
                ...prev,

                page_number:
                  nextMeta.page,

                total_pages:
                  nextMeta.totalPages,
              };
            },
          );
        } catch (e) {
          if (
            requestId !==
            requestRef.current
          ) {
            return;
          }

          setError(
            e?.response?.data
              ?.message ||
            e?.message ||
            'Failed to load shops',
          );

          setRows([]);

          setMeta(
            INITIAL_META,
          );

          setPage(
            (prev) => ({
              ...prev,
              total_pages: 1,
            }),
          );
        } finally {
          if (
            requestId ===
            requestRef.current
          ) {
            setLoading(
              false,
            );
          }
        }
      },
      [
        currentCompanyID,
        hasSpecificCompany,
        page.page_number,
        meta.pageSize,
        globalSearch,
        exactSearch,
        filters.status,
      ],
    );

  const handleSetGlobalSearch =
    useCallback(
      (
        valueOrUpdater,
      ) => {
        setGlobalSearch(
          (prev) =>
            typeof valueOrUpdater ===
              'function'
              ? valueOrUpdater(
                prev,
              )
              : valueOrUpdater,
        );

        setPage(
          (prev) =>
            prev.page_number ===
              1
              ? prev
              : {
                ...prev,
                page_number:
                  1,
              },
        );
      },
      [],
    );

  const handleSetExactSearch =
    useCallback(
      (
        valueOrUpdater,
      ) => {
        setExactSearch(
          (prev) =>
            typeof valueOrUpdater ===
              'function'
              ? valueOrUpdater(
                prev,
              )
              : valueOrUpdater,
        );

        setPage(
          (prev) =>
            prev.page_number ===
              1
              ? prev
              : {
                ...prev,
                page_number:
                  1,
              },
        );
      },
      [],
    );

  const handleSetFilters =
    useCallback(
      (
        valueOrUpdater,
      ) => {
        setFilters(
          (prev) =>
            typeof valueOrUpdater ===
              'function'
              ? valueOrUpdater(
                prev,
              )
              : valueOrUpdater,
        );

        setPage(
          (prev) =>
            prev.page_number ===
              1
              ? prev
              : {
                ...prev,
                page_number:
                  1,
              },
        );
      },
      [],
    );

  /*
   * 切换 company 时：
   * 1. 清搜索
   * 2. 清过滤
   * 3. 回第一页
   *
   * 保留你原来的业务行为。
   */
  useEffect(() => {
    if (
      previousCompanyIdRef
        .current ===
      currentCompanyID
    ) {
      return;
    }

    previousCompanyIdRef.current =
      currentCompanyID;

    setGlobalSearch('');

    setExactSearch(
      INITIAL_EXACT_SEARCH,
    );

    setFilters(
      INITIAL_FILTERS,
    );

    if (
      page.page_number !==
      1
    ) {
      skipNextFetchRef.current =
        true;

      setPage(
        (prev) => ({
          ...prev,
          page_number: 1,
        }),
      );
    }
  }, [
    currentCompanyID,
    page.page_number,
  ]);

  useEffect(() => {
    if (
      skipNextFetchRef.current
    ) {
      skipNextFetchRef.current =
        false;

      return;
    }

    fetchShops();
  }, [fetchShops]);

  const columns =
    useMemo(
      () => [
        {
          keys: [
            'id',
            'name',
          ],

          label:
            'Shop Name',

          render:
            (item) => (
              <div className="d-flex align-items-center">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip
                      id={`shop-${item.id}`}
                    >
                      ID:{' '}
                      {
                        item.id
                      }
                    </Tooltip>
                  }
                >
                  <span
                    style={{
                      cursor:
                        'pointer',
                    }}
                  >
                    {item.name}
                  </span>
                </OverlayTrigger>
              </div>
            ),
        },

        {
          key:
            'location',

          label:
            'Location',

          render:
            (item) =>
              item.location
                ?.name ||
              item.location
                ?.slug ||
              '-',
        },

        {
          key:
            'isActive',

          label:
            'Status',

          render:
            (item) => (
              <Badge
                bg={
                  item.isActive
                    ? 'success'
                    : 'danger'
                }
              >
                {item.isActive
                  ? 'Active'
                  : 'Inactive'}
              </Badge>
            ),
        },

        {
          key:
            'actions',

          label:
            'Actions',

          render:
            (item) => (
              <div
                className="d-flex gap-2 align-items-center"
                onClick={(
                  event,
                ) =>
                  event.stopPropagation()
                }
              >
                <NavLink
                  to={buildCompanyUrl(
                    `/shops/edit/${item.id}`,
                  )}
                  className="btn btn-view p-0"
                  title="View"
                >
                  <FaEye
                    size={20}
                  />
                </NavLink>

                <NavLink
                  to={buildCompanyUrl(
                    `/shops/edit/${item.id}`,
                  )}
                  className="btn btn-edit p-0"
                  title="Edit"
                >
                  <FaEdit
                    size={20}
                    style={{
                      marginLeft:
                        4,
                    }}
                  />
                </NavLink>
              </div>
            ),
        },
      ],
      [
        buildCompanyUrl,
      ],
    );

  return (
    <Container
      fluid
      className="d-flex flex-column"
    >
      <Row className="mb-4">
        <Col sm={8}>
          <span className="fw-semibold">
            Shops List
          </span>
        </Col>

        <Col
          sm={4}
          className="text-end"
        >
          <span className="fw-semibold">
            <NavLink
              to={buildCompanyUrl(
                '/dashboard',
              )}
              className="block-link"
            >
              Dashboard
            </NavLink>

            {' - Shops List'}
          </span>
        </Col>
      </Row>

      <Row>
        <Col sm={12}>
          <Card className="h-100 p-2">
            <Card.Body className="px-2 pt-2 pb-5">
              <Row>
                <Col md={10}>
                  <SmartTopTool
                    advancedSearch
                    globalSearch={
                      globalSearch
                    }
                    setGlobalSearch={
                      handleSetGlobalSearch
                    }
                    globalSearchPlaceholder="Search shop name or location..."
                    exactSearch={
                      exactSearch
                    }
                    setExactSearch={
                      handleSetExactSearch
                    }
                    exactSearchFields={
                      EXACT_SEARCH_FIELDS
                    }
                    filterOptions={
                      FILTER_OPTIONS
                    }
                    filters={
                      filters
                    }
                    setFilters={
                      handleSetFilters
                    }
                    debounceMs={
                      400
                    }
                  />
                </Col>

                {hasSpecificCompany && (
                  <Col
                    md={2}
                    className="d-flex justify-content-end"
                  >
                    <NavLink
                      to={buildCompanyUrl(
                        '/shops/new',
                      )}
                      className="border ms-1 btn-custom-success btn btn-primary"
                      title="New"
                      style={{
                        height:
                          40,
                      }}
                    >
                      + New Shop
                    </NavLink>
                  </Col>
                )}

                <Col md={12}>
                  <SmartTable
                    data={
                      rows
                    }
                    columns={
                      columns
                    }
                    loading={
                      loading
                    }
                    emptyState={
                      <div className="text-center py-5 text-muted">
                        <p>
                          {error ||
                            'No matching shops found'}
                        </p>
                      </div>
                    }
                  />
                </Col>

                <Col
                  md={12}
                  className="d-flex justify-content-between align-items-center"
                >
                  <span className="small text-muted">
                    Total:{' '}
                    {meta.total}
                  </span>

                  <SmartPagination
                    page={
                      page
                    }
                    setPage={
                      setPage
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ShopsList;